// ── 직원 수정 창: 창에 채울 값 · 저장 전 검사 · 서버로 보낼 값 (계산만, 화면·요청 없음) ──────────
// 2026-11-03 useUserEdit.js(300줄)에서 그대로 옮겼다(300줄 규칙, 동작 변화 없음).
//   검사 순서·알림 문구·보내는 칸은 옮기기 전과 같다. 아이디 최초 설정·퇴직 취소 확인·마케터 칸 빼기는 훅에 남는다.
import { todayKST } from '../../../utils/date';
import {
  normalizeDepartmentValue, normalizeAttendanceTimeType,
  formatPhone, splitEmailId, onlyDigits, toDate, toGmailEmail, isValidDateText, isNoId,
} from './utils';

// 적용일이 오늘보다 앞이면 정정, 뒤면 예약, 오늘이면 오늘부터
export function changeModeForDate(ymd) {
  const date = String(ymd || '').slice(0, 10);
  const today = todayKST();
  if (date < today) return 'correction';
  if (date > today) return 'scheduled';
  return 'today';
}

// 수정 창에 채울 값 — 서버에서 막 읽은 최신 정보(latest)로
export function editFormFromUser(latest, { attendanceTypeOptions, terminalFields }) {
  const attendanceType = normalizeAttendanceTimeType(latest.attendance_time_type, attendanceTypeOptions);
  return {
    userId: isNoId(latest.user_id) ? '' : latest.user_id || '',
    name: latest.name || '',
    department: normalizeDepartmentValue(latest.department),
    attendance_time_type: attendanceType,
    original_attendance_time_type: attendanceType,
    work_schedule_effective_from: todayKST(),
    work_schedule_change_mode: 'today',
    work_schedule_reason: '',
    department_effective_from: todayKST(),
    department_change_mode: 'today',
    department_change_reason: '',
    phone: formatPhone(latest.phone || ''),
    emailId: splitEmailId(latest.email || ''),
    birth_date: onlyDigits(latest.birth_date || '', 6),
    addressBase: latest.address || '',
    addressDetail: '',
    bank_name: latest.bank_name || '',
    bank_account: onlyDigits(latest.bank_account || '', 30),
    bank_effective_from: todayKST(),
    bank_change_mode: 'today',
    bank_change_reason: '',
    hire_date: toDate(latest.hire_date),
    resign_date: toDate(latest.resign_date),
    first_year_grant_count:
      latest.first_year_grant_count != null ? String(latest.first_year_grant_count) : '',
    memo: latest.memo || '',
    default_work_location: latest.default_work_location || '',
    ...terminalFields,
  };
}

// 저장 전 검사 + 보낼 값. 문제가 있으면 { error: 알림 문구 }, 없으면 { payload }.
//   2026-11-03 합치기-2(결정 5b): 3.3%(marketerRow)도 부서 필수 — 3.3% 부서 목록에서 고른다(예전엔 서버가 '마케팅' 고정).
export function buildSavePayload({ form, editing, marketerRow, attendanceTypeOptions }) {
  if (!form.name) return { error: '이름은 필수입니다.' };
  if (!form.department) return { error: '이름, 부서는 필수입니다.' };
  if (form.hire_date && !isValidDateText(form.hire_date)) return { error: '입사일자 형식이 올바르지 않습니다.' };
  if (form.resign_date && !isValidDateText(form.resign_date)) return { error: '퇴사일자 형식이 올바르지 않습니다.' };

  const nextAttendanceType = normalizeAttendanceTimeType(form.attendance_time_type, attendanceTypeOptions);
  const originalAttendanceType = normalizeAttendanceTimeType(form.original_attendance_time_type, attendanceTypeOptions);
  const attendanceTypeChanged = Boolean(editing) && nextAttendanceType !== originalAttendanceType;
  const nextDepartment = normalizeDepartmentValue(form.department);
  const originalDepartment = normalizeDepartmentValue(editing?.department);
  const departmentChanged = Boolean(editing) && nextDepartment !== originalDepartment;
  const nextBankName = String(form.bank_name || '').trim();
  const originalBankName = String(editing?.bank_name || '').trim();
  const nextBankAccount = onlyDigits(form.bank_account || '', 30);
  const originalBankAccount = onlyDigits(editing?.bank_account || '', 30);
  const bankChanged = Boolean(editing) && (nextBankName !== originalBankName || nextBankAccount !== originalBankAccount);
  if (attendanceTypeChanged && !isValidDateText(form.work_schedule_effective_from || '')) {
    return { error: '근무유형 적용일은 YYYY-MM-DD 형식이어야 합니다.' };
  }
  if (departmentChanged && !isValidDateText(form.department_effective_from || '')) {
    return { error: '부서 적용일은 YYYY-MM-DD 형식이어야 합니다.' };
  }
  if (bankChanged && !isValidDateText(form.bank_effective_from || '')) {
    return { error: '계좌 적용일은 YYYY-MM-DD 형식이어야 합니다.' };
  }
  if (form.first_year_grant_count) {
    const n = Number(form.first_year_grant_count);
    if (!Number.isInteger(n) || n <= 0 || n > 30) return { error: '연차 시작 차수는 1~30 정수로 입력해 주세요.' };
  }

  const fullAddress = `${form.addressBase} ${form.addressDetail}`.trim();
  const payload = {
    name:                  form.name,
    department:            nextDepartment,
    phone:                 form.phone || null,
    email:                 toGmailEmail(form.emailId),
    birth_date:            form.birth_date || null,
    address:               fullAddress || null,
    bank_name:             nextBankName || null,
    bank_account:          nextBankAccount || null,
    hire_date:             form.hire_date || null,
    resign_date:           form.resign_date || null,
    first_year_grant_count: form.first_year_grant_count ? Number(form.first_year_grant_count) : null,
    memo:                  form.memo || null,
  };
  if (!editing || attendanceTypeChanged) {
    payload.attendance_time_type = nextAttendanceType;
  }
  if (attendanceTypeChanged) {
    payload.work_schedule_effective_from = form.work_schedule_effective_from || todayKST();
    payload.work_schedule_change_mode = changeModeForDate(payload.work_schedule_effective_from);
    payload.work_schedule_reason = form.work_schedule_reason || null;
  }
  if (departmentChanged) {
    payload.department_effective_from = form.department_effective_from || todayKST();
    payload.department_change_mode = changeModeForDate(payload.department_effective_from);
    payload.department_change_reason = form.department_change_reason || null;
  }
  if (bankChanged) {
    payload.bank_effective_from = form.bank_effective_from || todayKST();
    payload.bank_change_mode = changeModeForDate(payload.bank_effective_from);
    payload.bank_change_reason = form.bank_change_reason || null;
  }
  return { payload };
}
